import React from "react";
import {
  BlockStack,
  Box,
  Card,
  Page,
  InlineGrid,
  SkeletonBodyText,
  SkeletonDisplayText,
} from "@shopify/polaris";

/** Table / list pages (Templates, Analytics overview card). */
export function ListPageSkeleton({ lines = 6, minHeight = 320 }) {
  return (
    <Box padding="500" minHeight={minHeight}>
      <BlockStack gap="400">
        <SkeletonBodyText lines={2} />
        <SkeletonBodyText lines={lines} />
      </BlockStack>
    </Box>
  );
}

/** Bar editor — nav tabs + form + preview columns. */
export function EditorPageSkeleton() {
  return (
    <Page fullWidth titleHidden>
      <BlockStack gap="300">
        <div className="fsb-editor-panel">
          <div className="fsb-editor-panel__header">
            <Box padding="400" paddingBlockEnd="300">
              <BlockStack gap="300">
                <div className="fsb-page-skel-nav">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className="fsb-page-skel-nav__pill" />
                  ))}
                </div>
                <SkeletonBodyText lines={1} />
              </BlockStack>
            </Box>
          </div>
          <div className="fsb-editor-panel__body">
            <div className="fsb-editor-split">
              <div className="fsb-editor-split__form">
                <Card>
                  <Box padding="400">
                    <BlockStack gap="400">
                      <SkeletonDisplayText size="small" />
                      <SkeletonBodyText lines={5} />
                      <SkeletonBodyText lines={3} />
                    </BlockStack>
                  </Box>
                </Card>
              </div>
              <div className="fsb-editor-split__preview fsb-editor-preview-sticky">
                <Card>
                  <Box padding="400">
                    <BlockStack gap="300">
                      <SkeletonDisplayText size="small" />
                      <Box minHeight="280px">
                        <SkeletonBodyText lines={10} />
                      </Box>
                    </BlockStack>
                  </Box>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </BlockStack>
    </Page>
  );
}

/** Settings / form pages with stacked cards. */
export function FormPageSkeleton({ sections = 2 }) {
  return (
    <Page>
      <BlockStack gap="400">
        {Array.from({ length: sections }, (_, i) => (
          <Card key={i}>
            <Box padding="400">
              <BlockStack gap="400">
                <SkeletonDisplayText size="small" />
                <SkeletonBodyText lines={4} />
              </BlockStack>
            </Box>
          </Card>
        ))}
      </BlockStack>
    </Page>
  );
}

/** Analytics dashboard — filters + metric tiles + charts. */
export function AnalyticsPageSkeleton() {
  return (
    <BlockStack gap="500">
      <Card>
        <ListPageSkeleton lines={1} minHeight={56} />
      </Card>
      <InlineGrid columns={{ xs: 1, sm: 2, md: 4 }} gap="400">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i}>
            <Box padding="400" minHeight="88px">
              <SkeletonBodyText lines={2} />
            </Box>
          </Card>
        ))}
      </InlineGrid>
      <InlineGrid columns={{ xs: 1, sm: 2, md: 4 }} gap="400">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i}>
            <Box padding="400" minHeight="88px">
              <SkeletonBodyText lines={2} />
            </Box>
          </Card>
        ))}
      </InlineGrid>
      <InlineGrid columns={{ xs: 1, md: 2 }} gap="400">
        <Card>
          <Box padding="400" minHeight="220px">
            <SkeletonBodyText lines={8} />
          </Box>
        </Card>
        <Card>
          <Box padding="400" minHeight="220px">
            <SkeletonBodyText lines={6} />
          </Box>
        </Card>
      </InlineGrid>
      <Card padding="0">
        <ListPageSkeleton lines={5} minHeight={200} />
      </Card>
    </BlockStack>
  );
}

/** Home / install bootstrap. */
export function DashboardPageSkeleton() {
  return (
    <Page>
      <BlockStack gap="500">
        <Card>
          <Box padding="500">
            <BlockStack gap="400">
              <SkeletonDisplayText size="medium" />
              <SkeletonBodyText lines={2} />
            </BlockStack>
          </Box>
        </Card>
        <Card>
          <Box padding="500" minHeight="240px">
            <BlockStack gap="400">
              <SkeletonDisplayText size="small" />
              <SkeletonBodyText lines={5} />
            </BlockStack>
          </Box>
        </Card>
        <Card>
          <Box padding="500">
            <SkeletonBodyText lines={4} />
          </Box>
        </Card>
      </BlockStack>
    </Page>
  );
}
